import React, { useState, useEffect } from 'react';
import ChatBox from '../../../features/askRexi/ChatBox';
import './AskRexi.css';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../services/supabaseClient';
import stringSimilarity from "string-similarity";
import AddEditLabModal from '../test-labs/AddEditLabModal';
import SearchLabsModal from "./SearchLabsModal";
import AddEditFactoryModal from '../factories/AddEditFactoryModal';
import SearchFactoriesModal from "./SearchFactoriesModal";
import AddEditProductModal from '../products/AddEditProductModal';
import SearchProductsModal from "./SearchProductsModal";
import SearchCitationCodesModal from "./SearchCitationCodesModal";
import RexiPopup from '../../../components/RexiPopup';
import FAQSection from "./FAQSection";
import "./FAQSection.css";

// 👇 Render field values per source (unchanged for extraction results)
const FieldRow = ({
  field,
  values,
}: {
  field: string;
  values: Record<string, any>;
}) => (
  <div className="analysis-field">
    <div className="analysis-field-name">{field}</div>
    {['regex', 'table', 'ai', 'resolved'].map((src) => {
      const entry = values[src];
      if (!entry) {
        return (
          <div key={src} className="analysis-source">
            <span className="analysis-source-label">{src}:</span>
            <span className="analysis-source-value">(none)</span>
          </div>
        );
      }

      const value = entry.value;
      const confidence = entry.confidence;

      const renderValue = (val: any) => {
        if (val === null || val === undefined) return '(none)';

        if (Array.isArray(val)) {
          if (val.length === 0) return '(none)';
          return (
            <ul className="analysis-list">
              {val.map((item: any, idx: number) => {
                if (item && typeof item === 'object' && 'value' in item) {
                  return <li key={idx}>{String(item.value)}</li>;
                }
                if (item && typeof item === 'object') {
                  return (
                    <li key={idx}>
                      <table className="analysis-mini-table">
                        <tbody>
                          {Object.entries(item).map(([k, v]) => (
                            <tr key={k}>
                              <td className="mini-key">{k}</td>
                              <td className="mini-val">{String((v as any)?.value ?? v ?? '')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </li>
                  );
                }
                return <li key={idx}>{String(item)}</li>;
              })}
            </ul>
          );
        }

        if (typeof val === 'object') {
          if ('value' in (val as any)) {
            return String((val as any).value ?? '');
          }
          return (
            <table className="analysis-mini-table">
              <tbody>
                {Object.entries(val).map(([k, v]) => (
                  <tr key={k}>
                    <td className="mini-key">{k}</td>
                    <td className="mini-val">{String((v as any)?.value ?? v ?? '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }

        return String(val);
      };

      return (
        <div key={src} className="analysis-source">
          <span className="analysis-source-label">{src}:</span>
          <span className="analysis-source-value">{renderValue(value)}</span>
          {confidence &&
            confidence !== 0 &&
            value !== null &&
            value !== undefined && (
              <span className="analysis-confidence">conf: {confidence}</span>
            )}
        </div>
      );
    })}
  </div>
);

const normalizeByField = (raw: any) => {
  const grouped: Record<string, any> = {};
  if (!raw || typeof raw !== 'object') return grouped;

  const sources = ['regex_report', 'table_report', 'ai_report', 'resolved_report'];
  sources.forEach((srcKey) => {
    const short = srcKey.replace('_report', '');
    const report = raw[srcKey]?.report || {};
    Object.entries(report).forEach(([field, val]: [string, any]) => {
      if (!grouped[field]) grouped[field] = {};

      if (Array.isArray(val)) {
        grouped[field][short] = {
          value: val.length > 0 ? val : null,
          confidence: null,
        };
      } else if (typeof val === 'object' && val !== null) {
        grouped[field][short] = {
          value: 'value' in val ? (val as any).value : val,
          confidence: (val as any).confidence ?? null,
        };
      } else {
        grouped[field][short] = { value: val, confidence: null };
      }
    });
  });
  return grouped;
};

const stepFields: Record<number, string[]> = {
  1: ["test_report_number", "test_report_date"],
  2: [
    "lab_name",
    "lab_cpsc_id",
    "lab_address_line_1",
    "lab_address_line_2",
    "lab_suite",
    "lab_city",
    "lab_state_province",
    "lab_postal_code",
    "lab_country",
    "lab_phone",
    "lab_email",
  ],
  3: [
    "factory_name",
    "factory_address_line_1",
    "factory_address_line_2",
    "factory_suite",
    "factory_city",
    "factory_state_province",
    "factory_postal_code",
    "factory_country",
    "factory_phone",
    "factory_email",
  ],
  4: ["products"], // later we’ll manage as an array
  5: ["citations"], // later we’ll manage as an array
};

const AskRexi = () => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null as File | null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showChatBox, setShowChatBox] = useState(true);
  const [analysisResult, setAnalysisResult] = useState(() => null);
  const [showExtractionDetail, setShowExtractionDetail] = useState(false);
  const [reportNumberConfirmed, setReportNumberConfirmed] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1 = Report Number
  const [confirmedFields, setConfirmedFields] = useState(() => ({} as Record<string, string>));
  const [tempReportNumber, setTempReportNumber] = useState(() => "");
  const [tempReportDate, setTempReportDate] = useState(() => "");
  const [resolvedMetadata, setResolvedMetadata] = useState(() => []);
  const [resolvedProducts, setResolvedProducts] = useState(() => []);
  const [resolvedCitations, setResolvedCitations] = useState(() => []);
  const [tempLabName, setTempLabName] = useState(() => "");
  const [tempLabCpscId, setTempLabCpscId] = useState(() => "");
  const [tempLabAddressLine1, setTempLabAddressLine1] = useState(() => "");
  const [tempLabAddressLine2, setTempLabAddressLine2] = useState(() => "");
  const [tempLabSuite, setTempLabSuite] = useState(() => "");
  const [tempLabCity, setTempLabCity] = useState(() => "");
  const [tempLabStateProvince, setTempLabStateProvince] = useState(() => "");
  const [tempLabPostalCode, setTempLabPostalCode] = useState(() => "");
  const [tempLabCountry, setTempLabCountry] = useState(() => "");
  const [tempLabPhone, setTempLabPhone] = useState(() => "");
  const [tempLabEmail, setTempLabEmail] = useState(() => "");
  const [tempFactoryName, setTempFactoryName] = useState(() => "");
  const [tempFactoryAddressLine1, setTempFactoryAddressLine1] = useState(() => "");
  const [tempFactoryAddressLine2, setTempFactoryAddressLine2] = useState(() => "");
  const [tempFactorySuite, setTempFactorySuite] = useState(() => "");
  const [tempFactoryCity, setTempFactoryCity] = useState(() => "");
  const [tempFactoryStateProvince, setTempFactoryStateProvince] = useState(() => "");
  const [tempFactoryPostalCode, setTempFactoryPostalCode] = useState(() => "");
  const [tempFactoryCountry, setTempFactoryCountry] = useState(() => "");
  const [tempFactoryPhone, setTempFactoryPhone] = useState(() => "");
  const [tempFactoryEmail, setTempFactoryEmail] = useState(() => "");
  const [tempProducts, setTempProducts] = useState(() => []);
  const [tempCitations, setTempCitations] = useState(() => []);
  const [showSavedPopup, setShowSavedPopup] = useState(false);
  const [savedPopupMsg, setSavedPopupMsg] = useState("");
  const [showClosingPopup, setShowClosingPopup] = useState(false);

  // 🧩 Multi-citation extraction states
  const [extractedCitations, setExtractedCitations] = useState(() => []);
  const [currentCitationIndex, setCurrentCitationIndex] = useState(0);
  const [selectedCitationCodes, setSelectedCitationCodes] = useState(() => []);

  const labName = resolvedMetadata[0]?.lab_name?.trim();
  const labAddress1 = resolvedMetadata[0]?.lab_address_line_1?.trim();
  const [selectedLabCpscId, setSelectedLabCpscId] = useState(() => null);
  const [selectedLabTestId, setSelectedLabNonCpscId] = useState(() => null);
  const [selectedLab, setSelectedLab] = useState(() => null);
  const [tempLabMatchedCertified, setTempLabMatchedCertified] = useState(() => null);
  const [tempLabMatchedNonCertified, setTempLabMatchedNonCertified] = useState(() => null);
  const [showAddLabModal, setShowAddLabModal] = useState(false);
  const [showSearchLabs, setShowSearchLabs] = useState(false);
  const [labDiscarded, setLabDiscarded] = useState(false);
  const [metadataReady, setMetadataReady] = useState(false);
  const [tempFactoryMatched, setTempFactoryMatched] = useState(() => null);
  const [showAddFactoryModal, setShowAddFactoryModal] = useState(false);
  const [factoryDiscarded, setFactoryDiscarded] = useState(false);
  const [showSearchFactories, setShowSearchFactories] = useState(false);
  const [selectedFactory, setSelectedFactory] = useState(() => null);
  const [selectedProducts, setSelectedProducts] = useState(() => []);
  const [showSearchProducts, setShowSearchProducts] = useState(false);
  const [showSearchCitationCodes, setShowSearchCitationCodes] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [tempProductName, setTempProductName] = useState(() => "");
  const [tempProductUPC, setTempProductUPC] = useState(() => "");
  const [tempProductCertificateType, setTempProductCertificateType] = useState(() => "");
  const [tempProductMatched, setTempProductMatched] = useState(() => null);
  const [productDiscarded, setProductDiscarded] = useState(false);

  // 🧩 Multi-product extraction states
  const [extractedProducts, setExtractedProducts] = useState(() => []);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [showAddProductOptions, setShowAddProductOptions] = useState(false);
  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);

  // 🧹 Reset Ask Rexi state between report uploads
  const resetAskRexi = () => {
    setUploadedFile(null);
    setAnalysisResult(null);
    setIsAnalyzing(false);
    setResolvedMetadata([]);
    setResolvedProducts([]);
    setResolvedCitations([]);
    setCurrentStep(1);
    setConfirmedFields({});
    setReportNumberConfirmed(false);
    setShowExtractionDetail(false);
    setTempReportNumber("");
    setTempReportDate("");
    setTempLabName("");
    setTempFactoryName("");
    setTempProducts([]);
    setTempCitations([]);
    setSelectedProducts([]);
    setSelectedCitationCodes([]);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name?.trim()) errors.name = 'Name is required';
    if (!formData.address_line_1?.trim()) errors.address_line_1 = 'Address Line 1 is required';
    if (!formData.city?.trim()) errors.city = 'City is required';
    if (!formData.country?.trim()) errors.country = 'Country is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateProductForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name?.trim()) errors.name = 'Product name is required';
    if (!formData.upc?.trim()) errors.upc = 'UPC code is required';
    if (!formData.certificate_type?.trim()) errors.certificate_type = 'Certificate type is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };





  const handleSaveTestLab = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      // 🧩 Prepare lab data
      const labData = {
        ...formData,
        auth_id: user?.id,
      };

      console.log("🧩 Prepared lab data:", labData);

      // 🧩 Insert new lab and capture ID
      const { data: insertedLab, error: insertError } = await supabase
        .from("test_labs")
        .insert([labData])
        .select("id")
        .single();

      if (insertError) throw insertError;

      const labId = insertedLab?.id;
      if (!labId) throw new Error("Lab insert failed — no ID returned.");
      console.log("✅ Lab inserted with id:", labId);

      // ----------------------------------------------------------
      // 🧩 DYNAMIC REPORT LOOKUP (same as factory)
      // ----------------------------------------------------------
      const reportId = await ensureReport();
      if (!reportId) throw new Error("No active report ID returned from ensureReport().");
      console.log("🧩 ensureReport() returned:", reportId);

      // 🧩 Link lab to report (use other_lab_id column)
      const { data, error: updateError } = await supabase
        .from("test_reports")
        .update({ other_lab_id: labId })
        .eq("id", reportId)
        .eq("auth_id", user?.id)
        .select("id, other_lab_id, report_number");

      console.log("🧩 Diagnostic update result:", {
        data,
        updateError,
        matched: Array.isArray(data) ? data.length : data ? 1 : 0,
      });

      if (updateError) {
        console.error("❌ Lab link update error:", updateError);
        alert("Lab link failed: " + updateError.message);
        return;
      }

      console.log("✅ Lab linked dynamically to report:", data);
    } catch (error) {
      console.error("❌ Error in handleSaveTestLab:", error);
      alert("Error saving lab: " + (error as any).message);
    } finally {
      setSaving(false);
    }
  };














  const handleSaveFactory = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      // 🧩 Prepare factory data
      const {
        name,
        address_line_1,
        address_line_2,
        suite,
        city,
        state_province,
        country,
        postal_code,
        telephone,
        email,
        status,
      } = formData;

      const factoryData = {
        name,
        address_line_1,
        address_line_2,
        suite,
        city,
        state_province,
        country,
        postal_code,
        telephone,
        email,
        status,
        auth_id: user?.id,
      };

      console.log("🧩 Prepared factory data:", factoryData);

      // 🧩 Insert new factory
      const { data: insertedFactory, error: insertError } = await supabase
        .from("factories")
        .insert([factoryData])
        .select("id")
        .single();

      if (insertError) throw insertError;
      const factoryId = insertedFactory?.id;
      if (!factoryId) throw new Error("Factory insert failed — no ID returned.");
      console.log("✅ Factory inserted with id:", factoryId);

      // ----------------------------------------------------------
      // 🧩 DYNAMIC REPORT LOOKUP (restore ensureReport)
      // ----------------------------------------------------------
      const reportId = await ensureReport();
      if (!reportId) throw new Error("No active report ID returned from ensureReport().");
      console.log("🧩 ensureReport() returned:", reportId);

      // 🧩 Link factory to dynamic report
      const { data, error: updateError } = await supabase
        .from("test_reports")
        .update({ factory_id: factoryId })
        .eq("id", reportId)
        .eq("auth_id", user?.id)
        .select("id, factory_id, report_number");

      console.log("🧩 Diagnostic update result:", {
        data,
        updateError,
        matched: Array.isArray(data) ? data.length : data ? 1 : 0,
      });


      if (updateError) {
        console.error("❌ Factory link update error:", updateError);
        alert("Factory link failed: " + updateError.message);
        return;
      }

      console.log("✅ Factory linked dynamically to report:", data);
    } catch (error) {
      console.error("❌ Error in handleSaveFactory:", error);
      alert("Error saving factory: " + (error as any).message);
    } finally {
      setSaving(false);
    }
  };















  // --- Product Save Handler (now persists + links to report + popup) ---
  const handleSaveProduct = async () => {
    if (!validateProductForm()) return;
    setSaving(true);
    try {
      const productData = {
        ...formData,
        auth_id: user?.id,
      };

      const { data, error } = await supabase
        .from("products")
        .insert([productData])
        .select("id, name, upc, certificate_type")
        .single();

      if (error) throw error;

      // Reflect in UI immediately
      // setSelectedProducts((prev: any[]) => [...(prev || []), data]);

      setConfirmedFields({
        ...confirmedFields,
        product_name: formData.name,
        product_upc: formData.upc ? String(formData.upc) : "",
        product_certificate_type: formData.certificate_type ?? "",
      });

      // Link to the test report (this also triggers the Rexi popup)
      await attachProductToReport(data.id);

      // Close modal and return to product step
      closeProductModal();
    } catch (error) {
      console.error("Error saving product:", error);
      alert("Error saving product: " + (error as any).message);
    } finally {
      setSaving(false);
    }
  };

  const [popupState, setPopupState] = useState({
    help: false,
    delete: false,
    modal: false,
    foreignKeyError: false
  });

  const [editingLab, setEditingLab] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    suite: '',
    city: '',
    state_province: '',
    country: '',
    postal_code: '',
    telephone: '',
    email: '',
    cpsc_id: '',
    status: 'active' as 'active' | 'inactive'
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const togglePopup = (popup: keyof typeof popupState, show: boolean) => {
    setPopupState((prev: typeof popupState) => ({ ...prev, [popup]: show }));
  };

  const openAddModal = (labData?: Partial<typeof formData>) => {
    setEditingLab(null);
    setFormData({
      name: labData?.name || '',
      address_line_1: labData?.address_line_1 || '',
      address_line_2: labData?.address_line_2 || '',
      suite: labData?.suite || '',
      city: labData?.city || '',
      state_province: labData?.state_province || '',
      country: labData?.country || '',
      postal_code: labData?.postal_code || '',
      telephone: labData?.telephone || '',
      email: labData?.email || '',
      cpsc_id: labData?.cpsc_id || '',
      status: 'active'
    });
    setFormErrors({});
    togglePopup('modal', true);
  };

  const closeModal = () => {
    setPopupState((prev: typeof popupState) => ({ ...prev, modal: false }));
    setEditingLab(null);
    setFormErrors({});
    setSaving(false);
  };

  const openAddFactoryModal = (factoryData?: Partial<typeof formData>) => {
    // 🧹 Always reset to a clean slate
    setFormData({
      name: "",
      address_line_1: "",
      address_line_2: "",
      suite: "",
      city: "",
      state_province: "",
      country: "",
      postal_code: "",
      telephone: "",
      email: "",
      status: "active",
    });

    // 🧩 Then apply any extracted or passed-in data
    if (factoryData) {
      setFormData((prev: typeof formData) => ({
        ...prev,
        ...factoryData,
      }));

    }

    setFormErrors({});
    setShowAddFactoryModal(true);
  };

  const closeFactoryModal = () => {
    setShowAddFactoryModal(false);
    setFormErrors({});
    setSaving(false);
  };









  // --- Product Modal Handlers ---
  const openAddProductModal = (productData?: Partial<typeof formData>) => {
    setEditingLab(null); // safe no-op reuse of pattern, keeps structure consistent
    setFormData({
      name: productData?.name || "",
      upc: productData?.upc || "",
      certificate_type: productData?.certificate_type || "CPC",
      status: productData?.status || "active",
    });
    setFormErrors({});
    setShowAddProductModal(true);
  };

  const closeProductModal = () => {
    setShowAddProductModal(false);
    setFormErrors({});
    setSaving(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev: typeof formData) => ({ ...prev, [field]: value }));
    if ((formErrors as any)[field]) {
      setFormErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  async function findCertifiedLabByCpscId(
    cpscId: string
  ): Promise<{ id: string; cpsc_id: string; name: string; address_line_1: string; state_province: string; country: string } | null> {
    if (!cpscId) return null;

    const { data, error } = await supabase
      .from("certified_labs")
      .select("id, cpsc_id, name, address_line_1, state_province, country")
      .eq("cpsc_id", cpscId)
      .maybeSingle();

    console.log("CPSC ID lookup", { cpscId, data, error });

    if (error || !data) {
      console.error("Supabase CPSC ID lookup error:", error);
      return null;
    }

    return data;
  }

  async function findCertifiedLabByFuzzy(
    labName: string,
    labAddr1: string
  ): Promise<{
    id: string;
    cpsc_id: string;
    name: string;
    address_line_1: string;
    state_province: string;
    country: string;
  } | null> {
    if (!labName && !labAddr1) return null;

    const { data, error } = await supabase
      .from("certified_labs")
      .select("id, cpsc_id, name, address_line_1, state_province, country");

    if (error || !data) return null;

    const names = data.map((d) => d.name?.toLowerCase() || "");
    const addrs = data.map((d) => d.address_line_1?.toLowerCase() || "");

    let bestIdx = -1;
    let bestScore = 0;

    if (labName) {
      const nameMatches = stringSimilarity.findBestMatch(labName.toLowerCase(), names);
      if (nameMatches.bestMatch.rating > bestScore) {
        bestScore = nameMatches.bestMatch.rating;
        bestIdx = nameMatches.bestMatchIndex;
      }
    }

    if (labAddr1) {
      const addrMatches = stringSimilarity.findBestMatch(labAddr1.toLowerCase(), addrs);
      if (addrMatches.bestMatch.rating > bestScore) {
        bestScore = addrMatches.bestMatch.rating;
        bestIdx = addrMatches.bestMatchIndex;
      }
    }

    // Return match if EITHER name or address > 0.7
    if (bestScore > 0.7 && bestIdx !== -1) {
      return data[bestIdx];
    }

    return null;
  }

  async function findNonCertifiedLabByFuzzy(
    labName: string,
    labAddr1: string
  ): Promise<{
    id: string;
    name: string;
    address_line_1: string;
    state_province: string;
    country: string;
  } | null> {
    // ✅ Guard against undefined, null, or empty strings
    if (
      (typeof labName !== "string" || labName.trim() === "") &&
      (typeof labAddr1 !== "string" || labAddr1.trim() === "")
    ) {
      return null;
    }

    const { data, error } = await supabase
      .from("test_labs")
      .select("id, name, address_line_1, state_province, country");

    if (error || !data) return null;
    if (data.length === 0) return null;

    const names = data
      .map((d) => (typeof d.name === "string" ? d.name.toLowerCase() : ""))
      .filter((s) => s.length > 0);
    const addrs = data
      .map((d) =>
        typeof d.address_line_1 === "string"
          ? d.address_line_1.toLowerCase()
          : ""
      )
      .filter((s) => s.length > 0);

    if (names.length === 0 && addrs.length === 0) return null;

    let bestIdx = -1;
    let bestScore = 0;

    if (typeof labName === "string" && labName.trim() && names.length > 0) {
      const nameMatches = stringSimilarity.findBestMatch(
        labName.toLowerCase(),
        names
      );
      if (nameMatches.bestMatch.rating > bestScore) {
        bestScore = nameMatches.bestMatch.rating;
        bestIdx = nameMatches.bestMatchIndex;
      }
    }

    if (typeof labAddr1 === "string" && labAddr1.trim() && addrs.length > 0) {
      const addrMatches = stringSimilarity.findBestMatch(
        labAddr1.toLowerCase(),
        addrs
      );
      if (addrMatches.bestMatch.rating > bestScore) {
        bestScore = addrMatches.bestMatch.rating;
        bestIdx = addrMatches.bestMatchIndex;
      }
    }

    if (bestScore > 0.7 && bestIdx !== -1) {
      return data[bestIdx];
    }

    return null;
  }

  async function findFactoryByFuzzy(factoryName: string, factoryAddr1: string) {
    if (
      (typeof factoryName !== "string" || factoryName.trim() === "") &&
      (typeof factoryAddr1 !== "string" || factoryAddr1.trim() === "")
    ) {
      return null;
    }

    const { data, error } = await supabase
      .from("factories")
      .select("id, name, address_line_1, state_province, country");

    if (error || !data || data.length === 0) return null;

    const names = data.map((d) => (d.name?.toLowerCase() || ""));
    const addrs = data.map((d) => (d.address_line_1?.toLowerCase() || ""));

    let bestIdx = -1;
    let bestScore = 0;

    if (factoryName) {
      const nameMatches = stringSimilarity.findBestMatch(factoryName.toLowerCase(), names);
      if (nameMatches.bestMatch.rating > bestScore) {
        bestScore = nameMatches.bestMatch.rating;
        bestIdx = nameMatches.bestMatchIndex;
      }
    }

    if (factoryAddr1) {
      const addrMatches = stringSimilarity.findBestMatch(factoryAddr1.toLowerCase(), addrs);
      if (addrMatches.bestMatch.rating > bestScore) {
        bestScore = addrMatches.bestMatch.rating;
        bestIdx = addrMatches.bestMatchIndex;
      }
    }

    if (bestScore > 0.7 && bestIdx !== -1) {
      return data[bestIdx];
    }

    return null;
  }

  async function findProductByFuzzy(productName: string, upc: string) {
    if (
      (typeof productName !== "string" || productName.trim() === "") &&
      (typeof upc !== "string" || upc.trim() === "")
    ) {
      return null;
    }

    const { data, error } = await supabase
      .from("products")
      .select("id, name, upc, certificate_type, status");

    if (error || !data || data.length === 0) return null;

    // Normalize fields
    const normalizedName = (productName || "").trim().toLowerCase();
    const normalizedUpc = String(upc || "").trim().toLowerCase();

    // Try exact UPC match first (fastest & safest)
    const exactUpcMatch = data.find(
      (d) =>
        d.upc &&
        String(d.upc).trim().toLowerCase() === normalizedUpc &&
        normalizedUpc.length > 0
    );
    if (exactUpcMatch) return exactUpcMatch;

    // Then fuzzy name match only if needed
    if (normalizedName) {
      const names = data.map((d) => d.name?.toLowerCase() || "");
      const nameMatches = stringSimilarity.findBestMatch(normalizedName, names);
      const { bestMatch, bestMatchIndex } = nameMatches;
      if (bestMatch.rating > 0.85) {
        return data[bestMatchIndex];
      }
    }

    // Otherwise, no confident match
    return null;
  }

  // --- Report persistence helpers ---
  const [reportId, setReportId] = useState(() => null);

  const showSaved = (msg: string) => {
    setSavedPopupMsg(`✅ ${msg} You can make changes anytime on the Test Reports page.`);
    setShowSavedPopup(true);
  };

  const upsertReportMeta = async (reportNumber: string, reportDate: string) => {
    try {
      if (reportId) {
        const { error } = await supabase
          .from("test_reports")
          .update({
            report_number: reportNumber || null,
            report_date: reportDate || null,
          })
          .eq("id", reportId)
          .eq("auth_id", user?.id);

        if (error) throw error;
        return reportId;
      } else {
        const { data, error } = await supabase
          .from("test_reports")
          .insert([
            {
              report_number: reportNumber || null,
              report_date: reportDate || null,
              auth_id: user?.id,
            },
          ])
          .select("id")
          .single();

        if (error) throw error;
        setReportId(data.id);
        return data.id;
      }
    } catch (err) {
      console.error("upsertReportMeta error:", err);
      alert("❌ Could not save test report meta.");
      return null;
    }
  };

  const ensureReport = async () => {
    if (reportId) return reportId;
    return await upsertReportMeta(tempReportNumber, tempReportDate);
  };




  const updateReportLab = async (opts: { certifiedLabId?: any; otherLabId?: any }) => {
    const id = await ensureReport();
    if (!id) return;

    const certifiedId = opts?.certifiedLabId;
    const otherId = opts?.otherLabId;

    // 🧩 Guard: skip if both IDs are invalid, undefined, or NaN
    if (
      (certifiedId === null || certifiedId === undefined || Number.isNaN(Number(certifiedId))) &&
      (otherId === null || otherId === undefined || Number.isNaN(Number(otherId)))
    ) {
      console.warn("🧩 Skipped updateReportLab — no valid labId provided:", opts);
      return;
    }

    // 🧩 Build payload safely (only include fields that have valid IDs)
    const payload: Record<string, number | null> = {};
    if (certifiedId && !Number.isNaN(Number(certifiedId))) payload.cpsc_lab_id = Number(certifiedId);
    if (otherId && !Number.isNaN(Number(otherId))) payload.other_lab_id = Number(otherId);

    const { error } = await supabase
      .from("test_reports")
      .update(payload)
      .eq("id", id)
      .eq("auth_id", user?.id);

    if (error) {
      console.error("updateReportLab error:", error);
      alert("❌ Could not save lab selection.");
      return;
    }

    showSaved("Test report updated with lab link.");
  };







  const updateReportFactory = async (factoryId: any) => {
    // 🧩 Guard: never send null or undefined factoryId to Supabase
    if (factoryId === null || factoryId === undefined || Number.isNaN(Number(factoryId))) {
      console.warn("🧩 Skipped updateReportFactory — invalid factoryId:", factoryId);
      return;
    }

    const id = await ensureReport();
    if (!id) return;

    // 🧩 Proceed only when we have both IDs
    const { error } = await supabase
      .from("test_reports")
      .update({ factory_id: Number(factoryId) })
      .eq("id", id)
      .eq("auth_id", user?.id);

    if (error) {
      console.error("updateReportFactory error:", error);
      alert("❌ Could not save factory selection.");
      return;
    }

    showSaved("Test report updated with factory link.");
  };







  const getProductId = (p: any) => {
    if (!p) return null;
    if (p.id) return p.id;
    if (p.matchedRecord && p.matchedRecord.id) return p.matchedRecord.id;
    return null;
  };

  const attachProductToReport = async (productId: any) => {
    const id = await ensureReport();
    if (!id || !productId) return;

    const { error } = await supabase
      .from("test_report_products")
      .insert([{ test_report_id: Number(id), product_id: Number(productId), auth_id: user?.id }]);

    if (error) {
      // Ignore duplicates if a unique constraint exists server-side
      if ((error as any).code === "23505") {
        // Duplicate product/report link — do not show popup
        return;
      }
      console.error("attachProductToReport error:", error);
      alert("❌ Could not add product to test report.");
      return;
    }

    // ✅ Only show popup when a new product was successfully linked
    showSaved("Test report updated with product(s).");
  };


  const ensureCitationId = async (citation: any) => {
    if (citation?.id) return citation.id;
    const code = citation?.citation_code || citation?.code;
    if (!code) return null;

    const { data, error } = await supabase
      .from("citation_codes")
      .select("id")
      .eq("citation_code", String(code).trim())
      .maybeSingle();

    if (error || !data) return null;
    return data.id;
  };

  const attachCitationToReport = async (citationId: any) => {
    const id = await ensureReport();
    if (!id || !citationId) return;

    const { error } = await supabase
      .from("test_report_citation_codes")
      .insert([{ test_report_id: Number(id), citation_code_id: Number(citationId), auth_id: user?.id }]);

    if (error) {
      if ((error as any).code !== "23505") {
        console.error("attachCitationToReport error:", error);
        alert("❌ Could not add citation code to test report.");
        return;
      }
    }
    showSaved("Test report updated with citation code(s).");
  };

  // keep tempReportNumber in sync once metadata is loaded
  useEffect(() => {
    if (resolvedMetadata.length > 0) {
      setTempReportNumber(resolvedMetadata[0]?.test_report_number || "");
      setTempReportDate(resolvedMetadata[0]?.test_report_date || "");
      setTempLabName(resolvedMetadata[0]?.lab_name || "");
      setTempLabCpscId(resolvedMetadata[0]?.lab_cpsc_id || "");
      setTempLabAddressLine1(resolvedMetadata[0]?.lab_address_line_1 || "");
      setTempLabAddressLine2(resolvedMetadata[0]?.lab_address_line_2 || "");
      setTempLabSuite(resolvedMetadata[0]?.lab_suite || "");
      setTempLabCity(resolvedMetadata[0]?.lab_city || "");
      setTempLabStateProvince(resolvedMetadata[0]?.lab_state_province || "");
      setTempLabPostalCode(resolvedMetadata[0]?.lab_postal_code || "");
      setTempLabCountry(resolvedMetadata[0]?.lab_country || "");
      setTempLabPhone(resolvedMetadata[0]?.lab_phone || "");
      setTempLabEmail(resolvedMetadata[0]?.lab_email || "");
      setTempFactoryName(resolvedMetadata[0]?.factory_name || "");
      setTempFactoryAddressLine1(resolvedMetadata[0]?.factory_address_line_1 || "");
      setTempFactoryAddressLine2(resolvedMetadata[0]?.factory_address_line_2 || "");
      setTempFactorySuite(resolvedMetadata[0]?.factory_suite || "");
      setTempFactoryCity(resolvedMetadata[0]?.factory_city || "");
      setTempFactoryStateProvince(resolvedMetadata[0]?.factory_state_province || "");
      setTempFactoryPostalCode(resolvedMetadata[0]?.factory_postal_code || "");
      setTempFactoryCountry(resolvedMetadata[0]?.factory_country || "");
      setTempFactoryPhone(resolvedMetadata[0]?.factory_phone || "");
      setTempFactoryEmail(resolvedMetadata[0]?.factory_email || "");
      setMetadataReady(true);
    }

    if (resolvedProducts.length > 0) {
      (async () => {
        // ✅ Keep your single-product fallback
        setTempProductName(resolvedProducts[0]?.product_name || resolvedProducts[0]?.name || "");
        setTempProductUPC(resolvedProducts[0]?.product_upc || resolvedProducts[0]?.upc || "");
        setTempProductCertificateType(
          resolvedProducts[0]?.product_certificate_type || resolvedProducts[0]?.certificate_type || ""
        );

        // ✅ Build array with fuzzy matches for each product
        const extractedList = await Promise.all(
          resolvedProducts.map(async (p: Record<string, any>) => {
            const name = p.product_name || p.name || "";
            const upc = p.product_upc || p.upc || "";

            // run existing fuzzy Supabase search
            const match = await findProductByFuzzy(name, upc);

            return {
              name,
              upc,
              certificate_type: p.product_certificate_type || p.certificate_type || "",
              isMatched: !!match,               // ✅ true if found
              matchedRecord: match || null,     // optional: preserve record
              status: p.status || "active",
            };
          })
        );

        setExtractedProducts(extractedList);
        setCurrentProductIndex(0);

      })(); // ← this async IIFE fixes the "await" error

      // 🧩 Populate extractedCitations (verified only via Supabase)
      if (resolvedCitations.length > 0) {
        (async () => {
          const matchedCitations: any[] = [];

          for (const c of resolvedCitations) {
            const extracted_code = c.citation_code || c.code || "";
            if (!extracted_code) continue; // skip blanks

            // 🔍 Lookup this citation code in Supabase
            const { data, error } = await supabase
              .from("citation_codes")
              .select("citation_code, citation_code_description, status")
              .eq("citation_code", extracted_code.trim())
              .eq("status", "active")
              .maybeSingle();

            if (error) {
              console.error("Citation code lookup error:", error);
              continue;
            }

            // ✅ Only keep matched, active Supabase citations
            if (data) {
              matchedCitations.push({
                citation_code: data.citation_code,
                citation_code_description: data.citation_code_description,
                isMatched: true,
                status: data.status || "active",
              });
            }
          }

          // ✅ Update state with matched results only
          setExtractedCitations(matchedCitations);
          setCurrentCitationIndex(0);
        })();
      }

    }

    const labName = resolvedMetadata[0]?.lab_name?.trim();
    const labAddress1 = resolvedMetadata[0]?.lab_address_line_1?.trim();

    // Reset default
    setTempLabMatchedNonCertified(false);

    // Only run if at least one usable value is present
    if (!resolvedMetadata[0]?.lab_cpsc_id && (labName || labAddress1)) {

      (async () => {
        const filters: string[] = [];

        if (labName) {
          filters.push(`name.ilike."${labName}"`);
        }
        if (labAddress1) {
          filters.push(`address_line_1.ilike."${labAddress1}"`);
        }

        if (filters.length > 0) {
          const { data, error } = await supabase
            .from('test_labs')
            .select('id, name, address_line_1, state_province, country')
            .eq('auth_id', user?.id)
            .or(filters.join(','))
            .or('cpsc_id.is.null,cpsc_id.eq.');

          if (error) {
            console.error('Supabase lab lookup error:', error);
            return;
          }

          if (data && data.length > 0) {
            setTempLabMatchedNonCertified(true);
          }
          console.log("Matched non-certified lab:", tempLabMatchedNonCertified);
        }
      })();

    }

    if (resolvedProducts && resolvedProducts.length > 0) {
      setTempProducts(resolvedProducts);
    } else {
      setTempProducts([]);
    }
    if (resolvedCitations && resolvedCitations.length > 0) {
      setTempCitations(resolvedCitations);
    } else {
      setTempCitations([]);
    }
  }, [resolvedMetadata]);

  useEffect(() => {
    if (tempLabCpscId) {
      findCertifiedLabByCpscId(tempLabCpscId).then((match) => {
        setTempLabMatchedCertified(match);
      });
    } else if (tempLabName && tempLabAddressLine1) {
      findCertifiedLabByFuzzy(tempLabName, tempLabAddressLine1).then((match) => {
        setTempLabMatchedCertified(match);
      });
    }
  }, [tempLabCpscId, tempLabName, tempLabAddressLine1]);

  useEffect(() => {
    if (!metadataReady) return; // 🛑 Don’t run until metadata is ready
    if (!tempLabName?.trim() && !tempLabAddressLine1?.trim()) return; // 🛑 Skip empty

    const runFuzzyCheck = async () => {
      try {
        const nonCertifiedMatch = await findNonCertifiedLabByFuzzy(
          tempLabName,
          tempLabAddressLine1
        );
        setTempLabMatchedNonCertified(nonCertifiedMatch);
      } catch (err) {
        console.error("Fuzzy match timing error:", err);
      }
    };

    runFuzzyCheck();
  }, [metadataReady, tempLabName, tempLabAddressLine1]);

  useEffect(() => {
    if (!metadataReady) return;
    if (!tempFactoryName?.trim() && !tempFactoryAddressLine1?.trim()) return;

    const runFuzzyCheck = async () => {
      try {
        const match = await findFactoryByFuzzy(tempFactoryName, tempFactoryAddressLine1);
        setTempFactoryMatched(match);
      } catch (err) {
        console.error("Factory fuzzy match error:", err);
      }
    };
    runFuzzyCheck();
  }, [metadataReady, tempFactoryName, tempFactoryAddressLine1]);

  useEffect(() => {
    if (!metadataReady) return;
    if (!tempProductName?.trim() && !tempProductUPC?.trim()) return;

    const runFuzzyCheck = async () => {
      try {
        const match = await findProductByFuzzy(tempProductName, tempProductUPC);
        setTempProductMatched(match);
      } catch (err) {
        console.error("Product fuzzy match error:", err);
      }
    };

    runFuzzyCheck();
  }, [metadataReady, tempProductName, tempProductUPC]);

  const { token, user } = useAuth();

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        setUploadedFile(file);
      } else {
        alert('Please upload a PDF file.');
      }
    }
  };

  const handleFileSelect = (e: any) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        setUploadedFile(file);
      } else {
        alert('Please upload a PDF file.');
      }
    }
  };

  const handleAnalyze = async () => {
    if (!uploadedFile) return;

    setIsAnalyzing(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

//    const n8nUrl = 'http://165.227.86.134:5678/webhook/test-report-intake';
      const n8nUrl = 'https://askrexi.comverex.ai/webhook/test-report-intake';

      const response = await fetch(n8nUrl, {
        method: 'POST',
        body: formData,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysisResult(data);

        const runId = data?.run_id ? String(data.run_id) : null;

        if (runId) {
          const [metaRes, prodRes, citRes] = await Promise.all([
            supabase
              .from('report_metadata')
              .select('*')
              .eq('run_id', runId)
              .eq('auth_id', user?.id)
              .eq('source', 'resolved'),
            supabase
              .from('report_products')
              .select('*')
              .eq('run_id', runId)
              .eq('auth_id', user?.id)
              .eq('source', 'resolved'),
            supabase
              .from('report_citations')
              .select('*')
              .eq('run_id', runId)
              .eq('auth_id', user?.id)
              .eq('source', 'resolved')
          ]);

          if (metaRes.data) setResolvedMetadata(metaRes.data);
          if (prodRes.data) setResolvedProducts(prodRes.data);
          if (citRes.data) setResolvedCitations(citRes.data);



        // 🧩 STEP: Record per-report usage in Stripe
        try {
          const billingResponse = await fetch('https://askrexi.comverex.ai/record-report-usage', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              auth_id: user?.id,
              run_id: runId,
            }),
          });

          const billingData = await billingResponse.json();
          if (billingResponse.ok && billingData?.success) {
            console.log(`✅ Report upload billed: ${billingData.usage_record_id}`);
          } else if (billingData?.skipped) {
            console.log(`ℹ️ Billing skipped: ${billingData.reason}`);
          } else {
            console.warn('⚠️ Billing request did not return success:', billingData);
          }
        } catch (billingError) {
          console.error('❌ Error recording usage billing:', billingError);
        }






        }
      } else {
        const errorText = await response.text();
        alert(`Analysis failed. Status: ${response.status}, ${errorText}`);
      }
    } catch (error: any) {
      alert('Error occurred while sending file to n8n. Check console for details.');
      console.error('Error sending file to n8n:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  const excludeKeys = [
    "created_at",
    "run_id",
    "auth_id",
    "extraction_id",
    "source",
    "metadata_id",
    "product_id",
    "citation_id"
  ];

  const effectiveFactory = (() => {
    const meta = resolvedMetadata[0] || {};

    const applicantIsUS =
      (meta.applicant_country &&
        meta.applicant_country.toLowerCase().includes("united states")) ||
      (meta.applicant_state_province &&
        /^[A-Z]{2}$/.test(meta.applicant_state_province));

    const hasFactory =
      meta.factory_name ||
      meta.factory_address_line_1 ||
      meta.factory_country;

    if (hasFactory) {
      return {
        factory_name: meta.factory_name,
        factory_address_line_1: meta.factory_address_line_1,
        factory_address_line_2: meta.factory_address_line_2,
        factory_city: meta.factory_city,
        factory_state_province: meta.factory_state_province,
        factory_country: meta.factory_country,
        factory_postal_code: meta.factory_postal_code,
        factory_email: meta.factory_email,
        factory_phone: meta.factory_phone,
      };
    }

    if (!applicantIsUS) {
      return {
        factory_name: meta.applicant_name,
        factory_address_line_1: meta.applicant_address_line_1,
        factory_address_line_2: meta.applicant_address_line_2,
        factory_city: meta.applicant_city,
        factory_state_province: meta.applicant_state_province,
        factory_country: meta.applicant_country,
        factory_postal_code: meta.applicant_postal_code,
        factory_email: meta.applicant_email,
        factory_phone: meta.applicant_contact,
      };
    }

    return {}; // no factory
  })();

  function hasMeaningfulMetadata(meta: any) {
    if (!meta) return false;

    const schemaFields = [
      "test_report_number",
      "test_report_date",
      "applicant_name",
      "applicant_contact",
      "applicant_address_line_1",
      "applicant_address_line_2",
      "applicant_suite",
      "applicant_city",
      "applicant_state_province",
      "applicant_postal_code",
      "applicant_country",
      "applicant_email",
      "factory_name",
      "factory_address_line_1",
      "factory_address_line_2",
      "factory_suite",
      "factory_city",
      "factory_state_province",
      "factory_postal_code",
      "factory_country",
      "factory_phone",
      "factory_email",
      "lab_name",
      "lab_cpsc_id",
      "lab_address_line_1",
      "lab_address_line_2",
      "lab_suite",
      "lab_city",
      "lab_state_province",
      "lab_postal_code",
      "lab_country",
      "lab_phone",
      "lab_email",
      "country_of_origin"
    ];

    return schemaFields.some(k => meta[k] !== null && meta[k] !== "" && meta[k] !== undefined);
  }

  return (
    <div className="data-table-container">
      <h1 className="data-table-title">
        <img src="/rexi-logo.png" alt="Rexi" className="page-title-rexi-logo" />
        Ask Rexi - Your CPSC eFiling Assistant
      </h1>

      <div className="ask-rexi-content">
        <p className="ask-rexi-subtitle">
          Get instant guidance on CPSC compliance, analyze test reports, and navigate eFiling requirements with confidence.
        </p>
      </div>

      <div className="test-report-analysis-section">
        <h2 className="section-title">Rexi Test Report Analysis</h2>
        <p className="section-description">
          Upload your test report and let Rexi analyze it for CPSC compliance requirements and identify any gaps.
        </p>

        <div
          className={`file-upload-area ${isDragOver ? 'drag-over' : ''} ${uploadedFile ? 'has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {!uploadedFile ? (
            <>
              <div className="upload-icon">📄</div>
              <h3>Drop your test report here</h3>
              <p>or</p>
              <label className="file-select-btn">
                Choose File
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>
              <p className="file-requirements">Supported format: PDF files only</p>
            </>
          ) : (
            <div className="uploaded-file-info">
              <div className="file-icon">📄</div>
              <div className="file-details">
                <h4>{uploadedFile.name}</h4>
                <p>{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button className="remove-file-btn" onClick={handleRemoveFile}>
                ✕
              </button>
            </div>
          )}
        </div>

        {uploadedFile && (
          <div className="analysis-actions">

            {!analysisResult && (
              <button
                className="analyze-btn"
                onClick={handleAnalyze}
                disabled={isAnalyzing}
              >
                {isAnalyzing ? 'Analyzing... may take a few minutes' : 'Analyze Report'}
              </button>
            )}

            {analysisResult && hasMeaningfulMetadata(resolvedMetadata[0]) && (
              <div className="progress-bar">
                {[
                  "Report # / Date",
                  "Test Lab",
                  "Factory",
                  "Products",
                  "Citation Codes",
                ].map((label, idx) => {
                  const stepNum = idx + 1;
                  const isActive = stepNum === currentStep;
                  const isCompleted = stepNum < currentStep;
                  return (
                    <div
                      key={label}
                      className={`progress-step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                    >
                      <div className="step-circle">{stepNum}</div>
                      <div className="step-label">{label}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {analysisResult && (
              <div className="analysis-output">
                {!hasMeaningfulMetadata(resolvedMetadata[0]) ? (
                  // ❌ Nothing extracted case
                  <>
                    <div className="status-banner">
                      <img src="/rexi-logo.png" alt="Rexi" className="banner-rexi-logo" />
                      {analysisResult && analysisResult.status === "invalid" && (
                        <span className="banner-text">
                          ❌ This does not appear to be a test report, if it is, it might be a scanned copy that will not allow reliable data extraction.
                          No concerns, I can walk you through the test report data entry process in the Test Reports section (see menu bar).
                        </span>
                      )}
                    </div>

                    <div style={{ textAlign: "center", marginTop: "16px" }}>
                      <button
                        className="analyze-btn shorter-btn"
                        onClick={() => {
                          setAnalysisResult(null);
                          setReportNumberConfirmed(false);
                          setCurrentStep(1);
                          setShowCancelPopup(true);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  // ✅ Normal guided flow
                  <>

                    {!reportNumberConfirmed ? (
                      <>
                        {/* ✅ Confirmed Fields First */}
                        {confirmedFields.test_report_number && (
                          <div className="report-extraction-confirmed">
                            <span className="confirmed-label">Saved Test Report Number:</span>
                            <span className="confirmed-value">{confirmedFields.test_report_number}</span>
                          </div>
                        )}

                        {confirmedFields.test_report_date && (
                          <div className="report-extraction-confirmed">
                            <span className="confirmed-label">Saved Test Report Date:</span>
                            <span className="confirmed-value">{confirmedFields.test_report_date}</span>
                          </div>
                        )}

                        {/* Show confirmed lab fields (works for both certified & non-certified) */}
                        {confirmedFields.lab_name && (
                          <div className="report-extraction-confirmed">
                            <span className="confirmed-label">Lab Name:</span>
                            <span className="confirmed-value">{confirmedFields.lab_name}</span>
                          </div>
                        )}

                        { }

                        {/* ✅ Show lab selected from SearchLabsModal above banner */}
                        {selectedLab && (
                          <div className="report-extraction-confirmed">
                            <span className="confirmed-label">Saved Lab:</span>
                            <span className="confirmed-value">{selectedLab.name}</span>
                          </div>
                        )}

                        {/* ✅ Show confirmed factory fields above banner */}
                        {confirmedFields.factory_name && (
                          <div className="report-extraction-confirmed">
                            <span className="confirmed-label">Saved Factory:</span>
                            <span className="confirmed-value">{confirmedFields.factory_name}</span>
                          </div>
                        )}

                        {/* ✅ Show factory selected or newly added above banner */}
                        {selectedFactory && (
                          <div className="report-extraction-confirmed">
                            <span className="confirmed-label">Saved Factory:</span>
                            <span className="confirmed-value">{selectedFactory.name}</span>
                          </div>
                        )}

                        {/* ✅ Show all products added so far above banner */}
                        {selectedProducts && selectedProducts.length > 0 && (
                          <>
                            {selectedProducts.map((product: any, index: number) => (
                              <div key={index} className="report-extraction-confirmed">
                                <span className="confirmed-label">
                                  {index === 0 ? "Saved Product:" : "Saved Product:"}
                                </span>
                                <span className="confirmed-value">{product.name}</span>
                              </div>
                            ))}
                          </>
                        )}

                        {/* ✅ Show all selected citation codes above banner */}
                        {selectedCitationCodes && selectedCitationCodes.length > 0 && (
                          <>
                            {selectedCitationCodes.map((code: any, index: number) => (
                              <div key={index} className="report-extraction-confirmed">
                                <span className="confirmed-label">
                                  {index === 0 ? "Saved Citation Code:" : "Saved Citation Code:"}
                                </span>
                                <span className="confirmed-value">{code.citation_code}</span>
                              </div>
                            ))}
                          </>
                        )}

                        <div className="status-banner">
                          <img
                            src="/rexi-logo.png"
                            alt="Rexi"
                            className="inline-rexi-logo banner-rexi-logo"
                          />
                          <span className="banner-text">
                            {currentStep === 2 ? (
                              // ✅ New top-priority case: user discarded lab
                              labDiscarded ? (
                                <>
                                  ✅ For children's products, click <strong>Search Labs</strong> and
                                  select a CPSC certified lab. For non-children's products, you can click{" "}
                                  <strong>Add Lab</strong> if the lab is not CPSC certified and you have
                                  not yet added it to your records.
                                </>
                              ) : // 1. No lab data at all
                                !(tempLabName ||
                                  tempLabAddressLine1 ||
                                  tempLabCity ||
                                  tempLabCountry ||
                                  tempLabPhone ||
                                  tempLabEmail ||
                                  tempLabCpscId) ? (
                                  <>❌ I could not extract any data on the test lab with confidence.</>
                                ) // 2. CPSC-certified lab (direct or fuzzy, validated in Supabase)
                                  : tempLabMatchedCertified ? (
                                    <>
                                      ✅ This appears to be a CPSC-certified lab (ID:{" "}
                                      {tempLabMatchedCertified.cpsc_id}). Please confirm this lab performed
                                      the testing.
                                    </>
                                  ) // 3. Non-certified lab already created by user
                                    : tempLabMatchedNonCertified ? (
                                      <>
                                        ✅ This appears to be a lab that is not CPSC-certified that you have
                                        created in your test lab data. Please confirm this lab performed the
                                        testing.
                                      </>
                                    ) // 4. Special case: ONLY a non-matching CPSC ID was extracted
                                      : tempLabCpscId &&
                                        !(
                                          tempLabName ||
                                          tempLabAddressLine1 ||
                                          tempLabCity ||
                                          tempLabCountry ||
                                          tempLabPhone ||
                                          tempLabEmail
                                        ) ? (
                                        <>❌ I could not extract any data on the test lab with confidence.</>
                                      ) : (
                                        // 5. Fallback: some lab data but no match
                                        <>⚠️ This is the data I found that may identify the lab.</>
                                      )
                            ) : currentStep === 3 ? (
                              // ✅ Factory confirmation flow (mirrors lab flow, no CPSC concept)
                              factoryDiscarded ? (
                                // 🗑️ User discarded the extracted factory data
                                <>
                                  ✅ Click <strong>Search Factories</strong> and select a factory you currently
                                  have in your records, or click <strong>Add Factory</strong> to add a factory
                                  to your records.
                                </>
                              ) : !(
                                tempFactoryName ||
                                tempFactoryAddressLine1 ||
                                tempFactoryCity ||
                                tempFactoryCountry ||
                                tempFactoryPhone ||
                                tempFactoryEmail
                              ) ? (
                                // 1️⃣ No factory data at all
                                <>❌ I could not extract any data on the factory with confidence.</>
                              ) : tempFactoryMatched ? (
                                // 2️⃣ Fuzzy match found in user's factories
                                <>
                                  ✅ This appears to be a factory you have already added in your records.
                                  Please confirm this factory produced the product(s).
                                </>
                              ) : (
                                // 3️⃣ Fallback — extracted data but no match
                                <>
                                  ⚠️ This is the data I found that may identify the factory.
                                </>
                              )

                            ) : currentStep === 4 ? (
                              // ✅ Multi-product confirmation flow
                              extractedProducts.length > 0 && currentProductIndex < extractedProducts.length ? (
                                (() => {
                                  const product = extractedProducts[currentProductIndex];
                                  if (productDiscarded) {
                                    return (
                                      <>
                                        ✅ Click <strong>Search Products</strong> and select a product you currently
                                        have in your records, or click <strong>Add Product</strong> to add a product
                                        to your records.
                                      </>
                                    );
                                  } else if (!product.name && !product.upc && !product.certificate_type) {
                                    return <>❌ I could not extract any product data with confidence.</>;
                                  } else if (product.isMatched) {
                                    return (
                                      <>
                                        ✅ This appears to be a product you have already added in your records.
                                        Please confirm this product applies to this test report.
                                      </>
                                    );
                                  } else {
                                    return <>⚠️ This is the data I found that may identify a product.</>;
                                  }
                                })()
                              ) : extractedProducts.length === 0 && selectedProducts.length === 0 ? (
                                <>
                                  ⚠️ I was not able to extract product data with confidence. Click{" "}
                                  <strong>Add Product</strong> to add a new product to your records,
                                  otherwise click <strong>Search Products</strong>.
                                </>
                              ) : (
                                <>
                                  ✅ There are no more products from the data I extracted. Click{" "}
                                  <strong>Add Another Product</strong> if needed, otherwise click{" "}
                                  <strong>Continue</strong> to move onto Citation Codes.
                                </>
                              )







                            ) : currentStep === 5 ? (
                              // ✅ Multi-citation confirmation flow
                              extractedCitations.length > 0 && currentCitationIndex < extractedCitations.length ? (
                                (() => {
                                  const citation = extractedCitations[currentCitationIndex];
                                  if (!citation.citation_code && !citation.citation_code_description) {
                                    return <>⚠️ I was not able to extract Citation Code data with confidence. Click{" "}
                                      <strong>Search Codes</strong> to add a Citation Code to this report.</>;
                                  } else if (citation.isMatched) {
                                    return <>✅ This appears to be a Citation Code in the report. Please confirm this citation applies to this test report.</>;
                                  } else {
                                    return <>⚠️ This Citation Code data could not be confidently matched.</>;
                                  }
                                })()
                              ) : extractedCitations.length === 0 && selectedCitationCodes.length === 0 ? (
                                <>⚠️ I was not able to extract Citation Code data with confidence. Click{" "}
                                  <strong>Search Codes</strong> to add a Citation Code to this report.</>
                              ) : (
                                <>✅ There are no more Citation Codes from the data I extracted. Click{" "}
                                  <strong>Search Codes</strong> if needed, otherwise click{" "}
                                  <strong>Finished</strong> to complete the report analysis process.</>
                              )








                            ) : resolvedMetadata[0]?.test_report_number &&
                              resolvedMetadata[0]?.test_report_date ? (<>
                                ✅ I found these as a test report number and date. Please double check
                                and correct if needed.
                              </>
                            ) : resolvedMetadata[0]?.test_report_number ? (
                              <>
                                ⚠️ I found this as a test report number but did not find a test report
                                date with confidence. Please double check the test report number and
                                correct if needed, please add the test report date.
                              </>
                            ) : resolvedMetadata[0]?.test_report_date ? (
                              <>
                                ⚠️ I found this as a test report date but I did not find a test report
                                number with confidence. Please double check the test report date and
                                correct if needed, please add the test report number.
                              </>
                            ) : (
                              <>
                                ❌ I did not find a test report number or date with confidence. Please
                                add the test report number and date.
                              </>
                            )}
                          </span>
                        </div>

                        {/* Show lab details if CPSC-certified (direct or fuzzy) */}
                        {currentStep === 2 && (tempLabCpscId || tempLabMatchedCertified) && (
                          <>
                            {tempLabMatchedCertified?.name && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Lab Name:</span>
                                <span className="confirmed-value">{tempLabMatchedCertified.name}</span>
                              </div>
                            )}
                            {tempLabMatchedCertified?.address_line_1 && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Address Line 1:</span>
                                <span className="confirmed-value">{tempLabMatchedCertified.address_line_1}</span>
                              </div>
                            )}
                            {tempLabMatchedCertified?.state_province && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">State/Province:</span>
                                <span className="confirmed-value">{tempLabMatchedCertified.state_province}</span>
                              </div>
                            )}
                            {tempLabMatchedCertified?.country && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Country:</span>
                                <span className="confirmed-value">{tempLabMatchedCertified.country}</span>
                              </div>
                            )}
                          </>
                        )}

                        {/* Show lab details if Non-CPSC (user-created match) */}
                        {currentStep === 2 && tempLabMatchedNonCertified && (
                          <>
                            {tempLabMatchedNonCertified?.name && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Lab Name:</span>
                                <span className="confirmed-value">{tempLabMatchedNonCertified.name}</span>
                              </div>
                            )}
                            {tempLabMatchedNonCertified?.address_line_1 && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Address Line 1:</span>
                                <span className="confirmed-value">{tempLabMatchedNonCertified.address_line_1}</span>
                              </div>
                            )}
                            {tempLabMatchedNonCertified?.state_province && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">State/Province:</span>
                                <span className="confirmed-value">{tempLabMatchedNonCertified.state_province}</span>
                              </div>
                            )}
                            {tempLabMatchedNonCertified?.country && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Country:</span>
                                <span className="confirmed-value">{tempLabMatchedNonCertified.country}</span>
                              </div>
                            )}
                          </>
                        )}

                        {/* ➕ NEW: Show extracted lab fields if no match */}
                        {currentStep === 2 && !tempLabMatchedCertified && !tempLabMatchedNonCertified && (
                          <>
                            {tempLabName && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Lab Name:</span>
                                <span className="confirmed-value">{tempLabName}</span>
                              </div>
                            )}
                            {tempLabAddressLine1 && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Address Line 1:</span>
                                <span className="confirmed-value">{tempLabAddressLine1}</span>
                              </div>
                            )}
                            {tempLabAddressLine2 && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Address Line 2:</span>
                                <span className="confirmed-value">{tempLabAddressLine2}</span>
                              </div>
                            )}
                            {tempLabSuite && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Suite:</span>
                                <span className="confirmed-value">{tempLabSuite}</span>
                              </div>
                            )}
                            {tempLabCity && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">City:</span>
                                <span className="confirmed-value">{tempLabCity}</span>
                              </div>
                            )}
                            {tempLabStateProvince && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">State/Province:</span>
                                <span className="confirmed-value">{tempLabStateProvince}</span>
                              </div>
                            )}
                            {tempLabPostalCode && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Postal Code:</span>
                                <span className="confirmed-value">{tempLabPostalCode}</span>
                              </div>
                            )}
                            {tempLabCountry && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Country:</span>
                                <span className="confirmed-value">{tempLabCountry}</span>
                              </div>
                            )}
                            {tempLabPhone && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Telephone:</span>
                                <span className="confirmed-value">{tempLabPhone}</span>
                              </div>
                            )}
                            {tempLabEmail && (
                              <div className="report-extraction-confirmed">
                                <span className="confirmed-label">Email:</span>
                                <span className="confirmed-value">{tempLabEmail}</span>
                              </div>
                            )}
                          </>
                        )}

                        {/* ✅ Show factory details below banner */}
                        {currentStep === 3 && (
                          <>
                            {tempFactoryMatched ? (
                              // ✅ Show matched factory from user's records
                              <>
                                {tempFactoryMatched.name && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Factory Name:</span>
                                    <span className="confirmed-value">{tempFactoryMatched.name}</span>
                                  </div>
                                )}
                                {tempFactoryMatched.address_line_1 && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Address Line 1:</span>
                                    <span className="confirmed-value">{tempFactoryMatched.address_line_1}</span>
                                  </div>
                                )}
                                {tempFactoryMatched.state_province && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">State/Province:</span>
                                    <span className="confirmed-value">{tempFactoryMatched.state_province}</span>
                                  </div>
                                )}
                                {tempFactoryMatched.country && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Country:</span>
                                    <span className="confirmed-value">{tempFactoryMatched.country}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              // ⚠️ Show extracted factory data if no match found
                              <>
                                {tempFactoryName && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Factory Name:</span>
                                    <span className="confirmed-value">{tempFactoryName}</span>
                                  </div>
                                )}
                                {tempFactoryAddressLine1 && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Address Line 1:</span>
                                    <span className="confirmed-value">{tempFactoryAddressLine1}</span>
                                  </div>
                                )}
                                {tempFactoryAddressLine2 && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Address Line 2:</span>
                                    <span className="confirmed-value">{tempFactoryAddressLine2}</span>
                                  </div>
                                )}
                                {tempFactorySuite && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Suite:</span>
                                    <span className="confirmed-value">{tempFactorySuite}</span>
                                  </div>
                                )}
                                {tempFactoryCity && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">City:</span>
                                    <span className="confirmed-value">{tempFactoryCity}</span>
                                  </div>
                                )}
                                {tempFactoryStateProvince && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">State/Province:</span>
                                    <span className="confirmed-value">{tempFactoryStateProvince}</span>
                                  </div>
                                )}
                                {tempFactoryPostalCode && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Postal Code:</span>
                                    <span className="confirmed-value">{tempFactoryPostalCode}</span>
                                  </div>
                                )}
                                {tempFactoryCountry && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Country:</span>
                                    <span className="confirmed-value">{tempFactoryCountry}</span>
                                  </div>
                                )}
                                {tempFactoryPhone && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Telephone:</span>
                                    <span className="confirmed-value">{tempFactoryPhone}</span>
                                  </div>
                                )}
                                {tempFactoryEmail && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Email:</span>
                                    <span className="confirmed-value">{tempFactoryEmail}</span>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        )}

                        {/* ✅ Show product details below banner */}
                        {currentStep === 4 && extractedProducts.length > 0 && currentProductIndex < extractedProducts.length && (
                          (() => {
                            const product = extractedProducts[currentProductIndex];
                            return (
                              <>
                                {product.isMatched ? (
                                  <>
                                    {product.name && (
                                      <div className="report-extraction-confirmed">
                                        <span className="confirmed-label">Product Name:</span>
                                        <span className="confirmed-value">{product.name}</span>
                                      </div>
                                    )}
                                    {product.upc && (
                                      <div className="report-extraction-confirmed">
                                        <span className="confirmed-label">UPC Code:</span>
                                        <span className="confirmed-value">{product.upc}</span>
                                      </div>
                                    )}
                                    {product.certificate_type && (
                                      <div className="report-extraction-confirmed">
                                        <span className="confirmed-label">Certificate Type:</span>
                                        <span className="confirmed-value">{product.certificate_type}</span>
                                      </div>
                                    )}
                                    {product.status && (
                                      <div className="report-extraction-confirmed">
                                        <span className="confirmed-label">Status:</span>
                                        <span className="confirmed-value">
                                          {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {product.name && (
                                      <div className="report-extraction-confirmed">
                                        <span className="confirmed-label">Product Name:</span>
                                        <span className="confirmed-value">{product.name}</span>
                                      </div>
                                    )}
                                    {product.upc && (
                                      <div className="report-extraction-confirmed">
                                        <span className="confirmed-label">UPC Code:</span>
                                        <span className="confirmed-value">{product.upc}</span>
                                      </div>
                                    )}
                                    {product.certificate_type && (
                                      <div className="report-extraction-confirmed">
                                        <span className="confirmed-label">Certificate Type:</span>
                                        <span className="confirmed-value">{product.certificate_type}</span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            );
                          })()
                        )}



                        {/* ✅ Show citation details below banner */}
                        {currentStep === 5 && (
                          (() => {
                            const citation = extractedCitations[currentCitationIndex] || {};

                            const codeValue =
                              citation.citation_code || citation.code || "";
                            const descriptionValue =
                              citation.citation_code_description || citation.description || "";

                            if (!codeValue && !descriptionValue) return null;

                            return (
                              <>
                                {codeValue && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Citation Code:</span>
                                    <span className="confirmed-value">{codeValue}</span>
                                  </div>
                                )}
                                {descriptionValue && (
                                  <div className="report-extraction-confirmed">
                                    <span className="confirmed-label">Description:</span>
                                    <span className="confirmed-value">{descriptionValue}</span>
                                  </div>
                                )}
                              </>
                            );
                          })()
                        )}







                        {/* Unconfirmed Fields Below Banner */}
                        {!confirmedFields.test_report_number && (
                          <div className="report-extraction-field">
                            <span className="report-extraction-label">Test Report Number:</span>
                            <input
                              type="text"
                              value={tempReportNumber}
                              onChange={(e) => setTempReportNumber(e.target.value)}
                              className="report-extraction-input"
                            />
                          </div>
                        )}

                        {!confirmedFields.test_report_date && (
                          <div className="report-extraction-field">
                            <span className="report-extraction-label">Test Report Date:</span>
                            <input
                              type="text"
                              value={tempReportDate}
                              onChange={(e) => setTempReportDate(e.target.value)}
                              className="report-extraction-input"
                            />
                          </div>
                        )}

                        {/* Buttons */}

                        <div style={{ textAlign: "center", marginTop: "32px" }}>

                          {currentStep === 2 ? (
                            <>
                              {/* Step 2 logic: Test Lab */}
                              {(tempLabMatchedCertified || tempLabMatchedNonCertified || tempLabName) && !labDiscarded ? (
                                // Case A: Lab match exists OR extracted data available
                                <>
                                  <button
                                    className="analyze-btn shorter-btn"

                                    onClick={async () => {
                                      console.log("Use This Lab clicked", { tempLabMatchedCertified, tempLabMatchedNonCertified });

                                      const selectedCertified = tempLabMatchedCertified;
                                      const selectedNonCertified = tempLabMatchedNonCertified;

                                      if (selectedCertified) {
                                        setConfirmedFields({
                                          ...confirmedFields,
                                          lab_name: selectedCertified.name,
                                          lab_address_line_1: selectedCertified.address_line_1,
                                          lab_state_province: selectedCertified.state_province,
                                          lab_country: selectedCertified.country,
                                        });
                                        await updateReportLab({ certifiedLabId: selectedCertified.id }); // 👈 persist
                                        setSelectedLabCpscId(selectedCertified.cpsc_id);
                                        setSelectedLabNonCpscId(null);
                                        setCurrentStep(3);
                                      } else if (selectedNonCertified) {
                                        setConfirmedFields({
                                          ...confirmedFields,
                                          lab_name: selectedNonCertified.name,
                                          lab_address_line_1: selectedNonCertified.address_line_1,
                                          lab_state_province: selectedNonCertified.state_province,
                                          lab_country: selectedNonCertified.country,
                                        });
                                        await updateReportLab({ otherLabId: selectedNonCertified.id }); // 👈 persist
                                        setSelectedLabNonCpscId(selectedNonCertified.id);
                                        setSelectedLabCpscId(null);
                                        setCurrentStep(3);
                                      } else if (
                                        tempLabName || tempLabAddressLine1 || tempLabCity || tempLabCountry || tempLabPhone || tempLabEmail
                                      ) {
                                        openAddModal({
                                          name: tempLabName || "",
                                          address_line_1: tempLabAddressLine1 || "",
                                          address_line_2: tempLabAddressLine2 || "",
                                          suite: tempLabSuite || "",
                                          city: tempLabCity || "",
                                          state_province: tempLabStateProvince || "",
                                          country: tempLabCountry || "",
                                          postal_code: tempLabPostalCode || "",
                                          telephone: tempLabPhone || "",
                                          email: tempLabEmail || "",
                                          status: "active",
                                        });
                                      } else {
                                        alert("No lab data available to use or add.");
                                      }
                                    }}
                                  >
                                    Use This Lab
                                  </button>

                                  <button
                                    className="analyze-btn shorter-btn discard-btn"
                                    onClick={() => {
                                      // Clear extracted/matched lab data
                                      setTempLabName("");
                                      setTempLabAddressLine1("");
                                      setTempLabAddressLine2("");
                                      setTempLabSuite("");
                                      setTempLabCity("");
                                      setTempLabStateProvince("");
                                      setTempLabCountry("");
                                      setTempLabPostalCode("");
                                      setTempLabPhone("");
                                      setTempLabEmail("");
                                      setTempLabMatchedCertified(null);
                                      setTempLabMatchedNonCertified(null);
                                      setLabDiscarded(true);
                                    }}
                                  >
                                    Discard This Lab
                                  </button>

                                  <button
                                    className="analyze-btn shorter-btn"
                                    onClick={() => {
                                      setAnalysisResult(null);
                                      setConfirmedFields({});
                                      setCurrentStep(1);
                                      setShowCancelPopup(true);
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                // Case B: No lab match or lab discarded
                                <>
                                  <button
                                    className="analyze-btn shorter-btn"
                                    onClick={() =>
                                      openAddModal(
                                        (tempLabName ||
                                          tempLabAddressLine1 ||
                                          tempLabCity ||
                                          tempLabCountry ||
                                          tempLabPhone ||
                                          tempLabEmail)
                                          ? {
                                            name: tempLabName || "",
                                            address_line_1: tempLabAddressLine1 || "",
                                            address_line_2: tempLabAddressLine2 || "",
                                            suite: tempLabSuite || "",
                                            city: tempLabCity || "",
                                            state_province: tempLabStateProvince || "",
                                            country: tempLabCountry || "",
                                            postal_code: tempLabPostalCode || "",
                                            telephone: tempLabPhone || "",
                                            email: tempLabEmail || "",
                                            status: "active",
                                          }
                                          : {}
                                      )
                                    }
                                  >
                                    Add Lab
                                  </button>

                                  <button
                                    className="analyze-btn shorter-btn"
                                    onClick={() => setShowSearchLabs(true)}
                                  >
                                    Search Labs
                                  </button>

                                  <button
                                    className="analyze-btn shorter-btn"
                                    onClick={() => {
                                      setAnalysisResult(null);
                                      setConfirmedFields({});
                                      setCurrentStep(1);
                                      setShowCancelPopup(true);
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                            </>
                          ) : currentStep === 3 ? (
                            <>
                              {/* Step 3 logic: Factory */}
                              {(tempFactoryMatched || tempFactoryName) && !factoryDiscarded ? (
                                // Case A – factory match exists OR extracted data available
                                <>
                                  <button
                                    className="analyze-btn shorter-btn"

                                    onClick={async () => {
                                      console.log("Use This Factory clicked", { tempFactoryMatched });
                                      const selected = tempFactoryMatched;

                                      if (selected) {
                                        setConfirmedFields({
                                          ...confirmedFields,
                                          factory_name: selected.name,
                                          factory_address_line_1: selected.address_line_1,
                                          factory_state_province: selected.state_province,
                                          factory_country: selected.country,
                                        });
                                        await updateReportFactory(selected.id); // 👈 persist
                                        setCurrentStep(4);
                                      } else if (
                                        tempFactoryName || tempFactoryAddressLine1 || tempFactoryCity || tempFactoryCountry || tempFactoryPhone || tempFactoryEmail
                                      ) {
                                        openAddFactoryModal({
                                          name: tempFactoryName || "",
                                          address_line_1: tempFactoryAddressLine1 || "",
                                          address_line_2: tempFactoryAddressLine2 || "",
                                          suite: tempFactorySuite || "",
                                          city: tempFactoryCity || "",
                                          state_province: tempFactoryStateProvince || "",
                                          country: tempFactoryCountry || "",
                                          postal_code: tempFactoryPostalCode || "",
                                          telephone: tempFactoryPhone || "",
                                          email: tempFactoryEmail || "",
                                          status: "active",
                                        });
                                      } else {
                                        alert("No factory data available to use or add.");
                                      }
                                    }}

                                  >
                                    Use This Factory
                                  </button>

                                  <button
                                    className="analyze-btn shorter-btn discard-btn"
                                    onClick={() => {
                                      // 🗑️ Clear extracted/matched factory data
                                      setTempFactoryName("");
                                      setTempFactoryAddressLine1("");
                                      setTempFactoryAddressLine2("");
                                      setTempFactorySuite("");
                                      setTempFactoryCity("");
                                      setTempFactoryStateProvince("");
                                      setTempFactoryCountry("");
                                      setTempFactoryPostalCode("");
                                      setTempFactoryPhone("");
                                      setTempFactoryEmail("");
                                      setTempFactoryMatched(null);
                                      setFactoryDiscarded(true);
                                    }}
                                  >
                                    Discard This Factory
                                  </button>

                                  <button
                                    className="analyze-btn shorter-btn"
                                    onClick={() => {
                                      setAnalysisResult(null);
                                      setConfirmedFields({});
                                      setCurrentStep(1);
                                      setShowCancelPopup(true);
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                // Case B – factory discarded or no match / no data
                                <>
                                  <button
                                    className="analyze-btn shorter-btn"
                                    // onClick={() => setShowAddFactoryModal(true)}
                                    onClick={() => openAddFactoryModal()} // Resets modal so lab data does not appear
                                  >
                                    Add Factory
                                  </button>

                                  <button
                                    className="analyze-btn shorter-btn"
                                    onClick={() => setShowSearchFactories(true)}
                                  >
                                    Search Factories
                                  </button>

                                  <button
                                    className="analyze-btn shorter-btn"
                                    onClick={() => {
                                      setAnalysisResult(null);
                                      setConfirmedFields({});
                                      setCurrentStep(1);
                                      setShowCancelPopup(true);
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                            </>

                          ) : currentStep === 4 ? (
                            <>
                              {/* Step 4 logic: Multi-Product Flow */}
                              <div className="product-actions">
                                {/* 🧩 CASE 1 — Reviewing extracted products */}
                                {extractedProducts.length > 0 && currentProductIndex < extractedProducts.length ? (
                                  <>
                                    <button
                                      className="analyze-btn shorter-btn"

                                      onClick={async () => {
                                        const product = extractedProducts[currentProductIndex];
                                        console.log("Use This Product clicked", product);

                                        if (product.isMatched) {
                                          // add to UI list
                                          setSelectedProducts((prev: any[]) => {
                                            const exists = prev.some(
                                              (p) =>
                                                p.name.trim().toLowerCase() === product.name.trim().toLowerCase() &&
                                                String(p.upc || "").trim() === String(product.upc || "").trim()
                                            );
                                            return exists ? prev : [...(prev || []), product];
                                          });

                                          // confirm fields
                                          setConfirmedFields({
                                            ...confirmedFields,
                                            product_name: product.name,
                                            product_upc: product.upc ? String(product.upc) : "",
                                            product_certificate_type: product.certificate_type || "",
                                          });

                                          // persist link
                                          const pid = getProductId(product);
                                          if (pid) await attachProductToReport(pid); // 👈 persist join

                                        } else if (product.name || product.upc) {
                                          openAddProductModal({
                                            name: product.name || "",
                                            upc: product.upc || "",
                                            certificate_type: product.certificate_type || "CPC",
                                            status: "active",
                                          });
                                        } else {
                                          alert("No product data available to use or add.");
                                        }

                                        setCurrentProductIndex((prev: number) => prev + 1);
                                      }}

                                    >
                                      Use This Product
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn discard-btn"
                                      onClick={() => {
                                        console.log("Discard Product clicked", extractedProducts[currentProductIndex]);
                                        setCurrentProductIndex((prev: number) => prev + 1);
                                      }}
                                    >
                                      Discard This Product
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => {
                                        setAnalysisResult(null);
                                        setConfirmedFields({});
                                        setCurrentStep(1);
                                        setShowCancelPopup(true);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </>

                                ) : extractedProducts.length === 0 && selectedProducts.length === 0 ? (
                                  // Case 2 — no extracted or added products yet
                                  <>
                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => openAddProductModal()}
                                    >
                                      Add Product
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => setShowSearchProducts(true)}
                                    >
                                      Search Products
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => {
                                        setAnalysisResult(null);
                                        setConfirmedFields({});
                                        setCurrentStep(1);
                                        setShowCancelPopup(true);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : !showAddProductOptions ? (
                                  // Case 3 — finished reviewing extracted OR added products
                                  <>
                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => setShowAddProductOptions(true)}
                                    >
                                      Add Another Product
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => setCurrentStep(5)}
                                    >
                                      Continue
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => {
                                        setAnalysisResult(null);
                                        setConfirmedFields({});
                                        setCurrentStep(1);
                                        setShowCancelPopup(true);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (

                                  <>
                                    {/* 🧩 CASE 4 — After clicking Add Another Product */}
                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => openAddProductModal()}
                                    >
                                      Add Product
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => setShowSearchProducts(true)}
                                    >
                                      Search Products
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => {
                                        setAnalysisResult(null);
                                        setConfirmedFields({});
                                        setCurrentStep(1);
                                        setShowCancelPopup(true);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                )}
                              </div>
                            </>



                          ) : currentStep === 5 ? (
                            <>
                              {/* Step 5 logic: Multi-Citation Flow */}
                              <div className="citation-actions">
                                {extractedCitations.length > 0 && currentCitationIndex < extractedCitations.length ? (
                                  (() => {
                                    const citation = extractedCitations[currentCitationIndex];
                                    return (
                                      <>
                                        <button
                                          className="analyze-btn shorter-btn"

                                          onClick={async () => {
                                            const citation = extractedCitations[currentCitationIndex];
                                            console.log("Use This Code clicked", citation);

                                            setSelectedCitationCodes((prev: any[]) => {
                                              const exists = prev.some((c) => {
                                                const existing = c.citation_code || c.code || "";
                                                const incoming = citation.citation_code || citation.code || "";
                                                return existing.trim().toLowerCase() === incoming.trim().toLowerCase();
                                              });
                                              return exists ? prev : [...(prev || []), citation];
                                            });

                                            setConfirmedFields({
                                              ...confirmedFields,
                                              citation_code: citation.citation_code,
                                              citation_description: citation.citation_code_description ?? "",
                                            });

                                            const cid = await ensureCitationId(citation);
                                            if (cid) await attachCitationToReport(cid); // 👈 persist join

                                            setCurrentCitationIndex((prev: number) => prev + 1);
                                          }}
                                        >
                                          Use This Code
                                        </button>

                                        <button
                                          className="analyze-btn shorter-btn discard-btn"
                                          onClick={() => setCurrentCitationIndex((prev: number) => prev + 1)}
                                        >
                                          Discard This Code
                                        </button>

                                        <button
                                          className="analyze-btn shorter-btn"
                                          onClick={() => {
                                            setAnalysisResult(null);
                                            setConfirmedFields({});
                                            setCurrentStep(1);
                                            setShowCancelPopup(true);
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </>
                                    );
                                  })()
                                ) : extractedCitations.length === 0 && selectedCitationCodes.length === 0 ? (
                                  <>
                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => setShowSearchCitationCodes(true)}
                                    >
                                      Search Codes
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => {
                                        setShowClosingPopup(true); // ✅ show Rexi closing popup
                                      }}
                                    >
                                      Finished
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => {
                                        setAnalysisResult(null);
                                        setConfirmedFields({});
                                        setCurrentStep(1);
                                        setShowCancelPopup(true);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => setShowSearchCitationCodes(true)}
                                    >
                                      Search Codes
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => {
                                        setShowClosingPopup(true); // ✅ show Rexi closing popup
                                      }}
                                    >
                                      Finished
                                    </button>

                                    <button
                                      className="analyze-btn shorter-btn"
                                      onClick={() => {
                                        setAnalysisResult(null);
                                        setConfirmedFields({});
                                        setCurrentStep(1);
                                        setShowCancelPopup(true);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                )}
                              </div>
                            </>

                          ) : (
                            // Fallback for other steps
                            <>

                              <button
                                className="analyze-btn shorter-btn"
                                onClick={async () => {
                                  const reportNumber = (tempReportNumber || "").trim();
                                  const reportDate = tempReportDate || "(none)";

                                  if (!reportNumber) {
                                    alert("Please enter a report number before continuing.");
                                    return;
                                  }

                                  // ✅ Duplicate report check before saving
                                  const { data: existingReports, error: checkError } = await supabase
                                    .from("test_reports")
                                    .select("id")
                                    .eq("report_number", reportNumber)
                                    .eq("auth_id", user?.id);

                                  if (checkError) {
                                    console.error("Duplicate check error:", checkError);
                                    return;
                                  }

                                  if (existingReports && existingReports.length > 0) {
                                    // ✅ Show popup and stop here
                                    setShowDuplicatePopup(true);
                                    return;
                                  }

                                  // ✅ Proceed normally if no duplicates
                                  setConfirmedFields({
                                    ...confirmedFields,
                                    test_report_number: reportNumber || "(none)",
                                    test_report_date: reportDate,
                                  });

                                  await upsertReportMeta(reportNumber, reportDate);
                                  showSaved("Test report record with report number and date saved.");
                                  setCurrentStep(2);
                                }}
                              >
                                Continue
                              </button>

                              <button
                                className="analyze-btn shorter-btn"
                                onClick={() => {
                                  setAnalysisResult(null);
                                  setConfirmedFields({});
                                  setCurrentStep(1);
                                  setShowCancelPopup(true);
                                }}
                              >
                                Cancel
                              </button>
                            </>
                          )}

                        </div>

                        <AddEditLabModal
                          isOpen={popupState.modal}
                          onClose={closeModal}
                          editingLab={editingLab}
                          formData={formData}
                          formErrors={formErrors}
                          saving={saving}
                          handleInputChange={handleInputChange}
                          handleSaveTestLab={handleSaveTestLab}
                          onAddLab={async (lab) => {
                            setSelectedLab(lab);
                            await updateReportLab({ otherLabId: lab.id }); // 👈 persist
                            setCurrentStep(3);
                          }}

                        />

                        <SearchLabsModal
                          isOpen={showSearchLabs}
                          onClose={() => setShowSearchLabs(false)}
                          onSelectLab={async (lab) => {
                            setSelectedLab(lab);

                            // ✅ Detect whether the lab is CPSC-certified
                            const isCpscCertified = !!lab.cpsc_id && lab.cpsc_id !== "";

                            try {
                              if (isCpscCertified) {
                                // use certified_labs.id (internal DB id)
                                await updateReportLab({ certifiedLabId: lab.id });
                              } else {
                                // use test_labs.id
                                await updateReportLab({ otherLabId: lab.id });
                              }

                              setCurrentStep(3);
                            } catch (error) {
                              console.error("SearchLabsModal onSelectLab error:", error);
                              alert("❌ Could not save lab selection.");
                            }
                          }}
                        />


                        <AddEditFactoryModal
                          isOpen={showAddFactoryModal}
                          onClose={() => setShowAddFactoryModal(false)}
                          editingFactory={null}
                          formData={formData}
                          formErrors={formErrors}
                          saving={saving}
                          handleInputChange={handleInputChange}
                          handleSaveFactory={handleSaveFactory}
                          onAddFactory={async (factory) => {
                            setConfirmedFields({
                              ...confirmedFields,
                              factory_name: factory.name,
                              factory_address_line_1: factory.address_line_1,
                              factory_state_province: factory.state_province ?? "",
                              factory_country: factory.country ?? "",
                            });
                            await updateReportFactory(factory.id); // 👈 persist
                            setShowAddFactoryModal(false);
                            setFactoryDiscarded(false);
                            setCurrentStep(4);
                          }}

                        />

                        <SearchFactoriesModal
                          isOpen={showSearchFactories}
                          onClose={() => setShowSearchFactories(false)}
                          onSelectFactory={async (factory) => {
                            setSelectedFactory(factory);
                            await updateReportFactory(factory.id); // 👈 persist
                            setShowSearchFactories(false);
                            setFactoryDiscarded(false);
                            setCurrentStep(4);
                          }}

                        />

                        <AddEditProductModal
                          isOpen={showAddProductModal}
                          onClose={() => {
                            setShowAddProductModal(false);
                            setShowAddProductOptions(false); // ✅ return to main product options
                          }}
                          editingProduct={null}
                          formData={formData}
                          formErrors={formErrors}
                          saving={saving}
                          handleInputChange={handleInputChange}
                          handleSaveProduct={handleSaveProduct}

                          onAddProduct={async (product) => {
                            setSelectedProducts((prev: any[]) => [...(prev || []), product]);

                            setConfirmedFields({
                              ...confirmedFields,
                              product_name: product.name,
                              product_upc: product.upc ? String(product.upc) : "",
                              product_certificate_type: product.certificate_type ?? "",
                            });

                            await attachProductToReport(product.id); // 👈 persist join

                            setShowAddProductModal(false);
                            setShowAddProductOptions(false);
                            setProductDiscarded(false);
                            setCurrentStep(4);
                          }}

                        />

                        <SearchProductsModal
                          isOpen={showSearchProducts}
                          onClose={() => {
                            setShowSearchProducts(false);
                            setShowAddProductOptions(false); // ✅ return to main product options
                          }}
                          onSelectProduct={async (product) => {
                            setSelectedProducts((prev: any[]) => {
                              const exists = prev.some(
                                (p) =>
                                  p.name.trim().toLowerCase() === product.name.trim().toLowerCase() &&
                                  String(p.upc || "").trim() === String(product.upc || "").trim()
                              );
                              return exists ? prev : [...(prev || []), product];
                            });

                            setConfirmedFields({
                              ...confirmedFields,
                              product_name: product.name,
                              product_upc: product.upc ? String(product.upc) : "",
                              product_certificate_type: product.certificate_type ?? "",
                            });

                            await attachProductToReport(product.id); // 👈 persist join

                            setShowSearchProducts(false);
                            setShowAddProductOptions(false);
                            setProductDiscarded(false);
                            setCurrentStep(4);
                          }}

                        />

                        <SearchCitationCodesModal
                          isOpen={showSearchCitationCodes}
                          onClose={() => {
                            setShowSearchCitationCodes(false);
                          }}

                          onSelectCitationCode={async (code) => {
                            setSelectedCitationCodes((prev: any[]) => {
                              const exists = prev.some(
                                (c) => c.citation_code.trim().toLowerCase() === code.citation_code.trim().toLowerCase()
                              );
                              return exists ? prev : [...(prev || []), code];
                            });

                            setConfirmedFields({
                              ...confirmedFields,
                              citation_code: code.citation_code,
                              citation_description: code.citation_code_description ?? "",
                            });

                            await attachCitationToReport(code.id); // 👈 persist join

                            setShowSearchCitationCodes(false);
                            setCurrentStep(5);
                          }}

                        />
                      </>

                    ) : (

                      <>
                        {!showExtractionDetail ? (
                          <button
                            className="analyze-btn"
                            onClick={() => setShowExtractionDetail(true)}
                          >
                            Extraction Detail
                          </button>
                        ) : (
                          <>
                            <h3>Report Extraction Results</h3>
                            {Object.entries(normalizeByField(analysisResult)).map(([field, values]) => (
                              <FieldRow key={field} field={field} values={values as any} />
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="faq-section">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <FAQSection />
      </div>

      <div className="floating-chat-container">
        <button
          className={`chat-toggle-btn ${showChatBox ? 'active' : ''}`}
          onClick={() => setShowChatBox(!showChatBox)}
        >
          💬
        </button>
        {showChatBox && (
          <div className="floating-chat-box">
            <div className="chat-header">
              <div className="chat-header-content">
                <div className="chat-header-top">
                  <img src="/rexi-logo.png" alt="Rexi" className="chat-rexi-logo" />
                  <span>Ask Rexi</span>
                </div>
                <div className="chat-disclaimer">
                  Rexi may add external AI knowledge in her answers. Check important information.
                </div>
              </div>
              <button className="chat-close-btn" onClick={() => setShowChatBox(false)}>✕</button>
            </div>
            <ChatBox />
          </div>
        )}
      </div>
      {showDuplicatePopup && (
        <RexiPopup
          message="❌ This report number already exists. If you would like to have it analyzed, please remove this report on the Test Reports page or use a different report number."
          onClose={() => setShowDuplicatePopup(false)}
        />
      )}
      {showSavedPopup && (
        <RexiPopup
          message={savedPopupMsg}
          onClose={() => setShowSavedPopup(false)}
        />
      )}
      {showCancelPopup && (
        <RexiPopup
          message="⚠️ The current report analysis session has been cancelled. You can update any saved data from this session on the Test Reports page."
          onClose={() => {
            setShowCancelPopup(false);
            setTimeout(() => resetAskRexi(), 100); // 🧹 Reset all Ask Rexi state after successful completion
          }}
        />
      )}
      {showClosingPopup && (
        <RexiPopup
          message="✅ The report analysis and data uploading process has been completed. You can update this data anytime on the Test Reports page."
          onClose={() => {
            setShowClosingPopup(false);
            setTimeout(() => resetAskRexi(), 100); // 🧹 Reset all Ask Rexi state after successful completion
          }}
        />
      )}
    </div>
  );
};

export default AskRexi;
